import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEO from '../components/common/SEO';

const Section = ({ title, children }) => (
  <section className="mb-8" data-testid={`privacy-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <h2 className="font-heading text-2xl text-white mb-3">{title}</h2>
    <div className="text-white/75 leading-relaxed space-y-3">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-6 space-y-2 marker:text-[#FF007F]">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO
        title="Privacy Policy — Universal Performing Arts (UPA)"
        description="How Universal Performing Arts (UPA) collects, uses, stores and protects personal data for students, parents and enquirers, in line with UK GDPR."
        canonical="https://www.upaschool.com/privacy-policy"
      />
      <Navbar />

      <main className="max-w-3xl mx-auto px-6 lg:px-0 py-12 lg:py-20" data-testid="privacy-page">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#FF007F] transition-colors text-sm mb-8"
          data-testid="privacy-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <header className="mb-10">
          <p className="text-[#FF007F] text-sm uppercase tracking-widest mb-3">Legal</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-white/60 text-base">
            Universal Performing Arts (UPA) — how we collect, use and protect your personal information.
            Last updated: June 2026.
          </p>
        </header>

        <Section title="Who We Are">
          <p>
            Universal Performing Arts (&quot;UPA&quot;, &quot;we&quot;, &quot;us&quot;) is a performing arts school based in Milton Keynes,
            United Kingdom, providing dance, drama, singing and performance classes, workshops, camps and events for
            children and adults. UPA is the data controller for the personal information described in this policy.
          </p>
          <p>
            You can contact us about anything in this policy at{' '}
            <a href="mailto:theteam@upaschool.com" className="text-[#FF007F] hover:underline">theteam@upaschool.com</a>{' '}
            or on 07310 105678.
          </p>
        </Section>

        <Section title="Information We Collect">
          <p>Depending on how you interact with us, we may collect:</p>
          <Bullets
            items={[
              'Parent/guardian details — name, email address, phone number and billing details.',
              'Student details — name, date of birth/age, class enrolments, attendance records and progress information.',
              'Health and safety information you choose to share — such as medical conditions, allergies or emergency contact details, so we can keep students safe during classes.',
              'Enquiry and booking details — trial requests, camp and workshop bookings, waitlist requests and messages you send us through our website forms.',
              'Payment information — payments are processed securely by Stripe; we never see or store your full card number. We keep records of what has been paid and, where you choose to save a card, a secure reference to it held by Stripe.',
              'Social media enquiries — if you respond to one of our adverts or lead forms on Facebook or Instagram, Meta shares the details you submitted (such as your name, phone number, email and your answers) with us. These are stored securely in our private admin system and used to respond to your enquiry, arrange trial bookings and send follow-up communications about the classes you asked about.',
              'Account and technical information — login details for parent/staff accounts and basic technical data needed to keep the site secure and working.',
            ]}
          />
        </Section>

        <Section title="How We Use Your Information">
          <Bullets
            items={[
              'To manage enrolments, class registers, attendance and student progress.',
              'To arrange and follow up free trials, camps, workshops and events you have asked about.',
              'To take payments, manage fees, renewals and refunds, and send booking and payment confirmations.',
              'To contact you about your child\u2019s classes — including by email, phone or WhatsApp where you have provided your number in an enquiry or booking.',
              'To respond to enquiries made through our website or through Facebook/Instagram lead forms.',
              'To keep students safe, including using medical and emergency contact information you have provided.',
              'To meet our legal and accounting obligations.',
            ]}
          />
          <p>
            We rely on the following lawful bases under UK GDPR: performance of a contract (providing classes you have
            booked), legitimate interests (responding to enquiries you have made and running our school effectively),
            legal obligation (tax and accounting records) and consent where required (for example, marketing messages —
            you can withdraw consent at any time).
          </p>
        </Section>

        <Section title="Children's Information">
          <p>
            Most of our students are children, so we take particular care with their information. Student details are
            always provided to us by a parent or guardian, are used only for running classes safely and effectively, and
            are never used for marketing directly to children. Any photography or filming of students is covered
            separately in our Terms &amp; Conditions and is subject to parental permission.
          </p>
        </Section>

        <Section title="Who We Share Information With">
          <p>We never sell your personal information. We share it only with trusted services we use to run UPA:</p>
          <Bullets
            items={[
              'Stripe — our payment processor, for taking and managing payments securely.',
              'Meta (Facebook/Instagram) — when you submit one of their lead forms, Meta processes your details under its own privacy policy before passing them to us.',
              'Email providers — to send you booking confirmations, receipts and class updates.',
              'Our website and database hosting providers — who store data securely on our behalf.',
              'Authorities where we are legally required to do so.',
            ]}
          />
        </Section>

        <Section title="How Long We Keep Your Information">
          <Bullets
            items={[
              'Active student and parent records — for as long as the student attends UPA.',
              'After a student leaves — records are archived and then removed when no longer needed.',
              'Enquiries and trial requests that do not lead to enrolment — kept for a reasonable period so we can follow up, then deleted.',
              'Payment and financial records — kept for 6 years as required by UK tax law.',
              'You can request deletion of your information at any time — see our Data Deletion Instructions page.',
            ]}
          />
        </Section>

        <Section title="How We Protect Your Information">
          <p>
            Our website and parent/staff portal use encrypted (HTTPS) connections. Access to personal information is
            restricted to authorised UPA staff with individual password-protected accounts. Card payments are handled
            entirely by Stripe, a PCI-DSS compliant payment provider.
          </p>
        </Section>

        <Section title="Cookies and Local Storage">
          <p>
            Our website uses only essential browser storage — for example, to keep you logged in to the parent or staff
            portal. We do not use third-party advertising or tracking cookies on this site. If you arrive via a Facebook
            or Instagram advert, Meta's own cookies and tracking apply on their platforms under their policies.
          </p>
        </Section>

        <Section title="Your Rights">
          <p>Under UK GDPR you have the right to:</p>
          <Bullets
            items={[
              'Access a copy of the personal information we hold about you or your child.',
              'Ask us to correct information that is inaccurate or incomplete.',
              'Ask us to delete information we no longer need to keep.',
              'Object to, or ask us to restrict, certain uses of your information.',
              'Withdraw consent at any time where we rely on it (for example, marketing).',
              'Complain to the Information Commissioner\u2019s Office (ICO) at ico.org.uk if you are unhappy with how we have handled your data.',
            ]}
          />
          <p>
            To exercise any of these rights, email{' '}
            <a href="mailto:theteam@upaschool.com" className="text-[#FF007F] hover:underline">theteam@upaschool.com</a>.
            We will respond within one month.
          </p>
        </Section>

        <Section title="Changes to This Policy">
          <p>
            We may update this policy from time to time. The latest version will always be published on this page with
            the date it was last updated.
          </p>
        </Section>

        <p className="text-white/40 text-sm mt-12">
          See also our{' '}
          <Link to="/terms-and-conditions" className="text-[#FF007F] hover:underline">Terms &amp; Conditions</Link>.
        </p>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
